// View-models that turn the real engine docs (Entry + Fixtures + Group) into the
// shapes the round/standings screens render. All scoring uses the shared pure engine
// (Build-Your-Card §7) so the numbers shown match exactly what the server settled.
import {
  anchorHit,
  cardMultiplier,
  cardScore,
  CONFIG,
  deriveResult,
  getMarket,
  gradeLeg,
  isEntryEditable,
  legMultiplier,
  riskTier,
  scorerIdsFromEvents,
  type Entry,
  type Fixture,
  type FixtureResult,
  type Outcome,
  type Prediction,
  type PredictionLeg,
  type Round
} from '@h2h/shared';
import { formatMultiplier } from '../format';
import type { CardVM, LegVM, MarketHit, PickStatus, TrailStep } from './types';

const BASE = CONFIG.COMBO.BASE_POINTS;

/** A short team code: the stored one, else the first three letters upper-cased. */
function teamCode(name: string, code?: string): string {
  if (code) return code;
  return name.replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase() || name.slice(0, 3).toUpperCase();
}

function isTerminal(f: Fixture): boolean {
  return f.status === 'SETTLED' || f.status === 'VOID';
}

/** The 1X2 base, in words, relative to the two teams. */
function outcomeLabel(outcome: Outcome, homeTeam: string, awayTeam: string): string {
  if (outcome === 'HOME') return `${homeTeam} to win`;
  if (outcome === 'AWAY') return `${awayTeam} to win`;
  return 'Draw';
}

/** The display detail for a boost's selection (option label, scorer name, score). */
function legDetail(leg: PredictionLeg): string | undefined {
  const m = getMarket(leg.marketId);
  if (!m) return undefined;
  if (m.kind === 'player') return leg.label ?? 'Scorer';
  if (m.kind === 'exact') return leg.selection;
  return m.options?.find((o) => o.value === leg.selection)?.label ?? leg.selection;
}

/** Resolve one boost for display against a result (or none yet, when upcoming). */
function legVM(leg: PredictionLeg, r: FixtureResult | null, live: boolean): LegVM {
  const m = getMarket(leg.marketId);
  const multiplier = legMultiplier(leg.marketId, leg.selection);
  let state: MarketHit = 'open';
  if (r) {
    const g = gradeLeg(leg, r);
    if (g.gradeable) state = g.hit ? (live ? 'ontrack' : 'hit') : live ? 'offtrack' : 'miss';
  }
  const missed = state === 'miss' || state === 'offtrack';
  return {
    marketId: leg.marketId,
    name: m?.label ?? leg.marketId,
    detail: legDetail(leg),
    multiplier,
    tier: riskTier(multiplier),
    state,
    factorLabel: `${missed ? '÷' : '×'}${formatMultiplier(multiplier)}`
  };
}

/** The flat dot row: the base dot, then one per boost. */
function dotRow(anchor: MarketHit, legs: LegVM[]): MarketHit[] {
  return [anchor, ...legs.map((l) => l.state)];
}

/** The settled "how it stacked" trail: base activation, then each boost in turn. */
function buildTrail(p: Prediction, r: FixtureResult, legs: LegVM[], outLabel: string): TrailStep[] {
  const baseHit = anchorHit(p, r);
  const steps: TrailStep[] = [
    {
      label: outLabel,
      factorLabel: baseHit ? `+${BASE}` : `−${BASE}`,
      running: baseHit ? BASE : -BASE,
      state: baseHit ? 'hit' : 'miss'
    }
  ];
  if (!baseHit) return steps; // a busted base ends the card at −BASE
  let running = BASE;
  (p.legs ?? []).forEach((leg, i) => {
    const g = gradeLeg(leg, r);
    if (!g.gradeable) return; // excluded boost — no effect on the stack
    running *= g.factor;
    steps.push({
      label: legs[i]?.name ?? leg.marketId,
      factorLabel: legs[i]?.factorLabel ?? '×1',
      running: Math.round(running),
      state: legs[i]?.state ?? 'open'
    });
  });
  return steps;
}

function finalSummary(card: CardVM): string {
  if (card.anchorState === 'miss') return `Base missed · ${card.points}`;
  const correct = card.legs.filter((l) => l.state === 'hit').length;
  const gradeable = card.legs.filter((l) => l.state !== 'open').length;
  if (card.legs.length === 0) return 'Base hit · no boosts';
  if (correct === gradeable && gradeable > 0) return `Base hit · all ${gradeable} boosts landed`;
  return `Base hit · ${correct} of ${gradeable} boosts`;
}

function liveSummary(card: CardVM): string {
  if (card.anchorState === 'offtrack') return 'Base trailing — at risk';
  const onTrack = card.legs.filter((l) => l.state === 'ontrack').length;
  const gradeable = card.legs.filter((l) => l.state !== 'open').length;
  if (card.legs.length === 0) return 'Base on track';
  return `Base up · ${onTrack} of ${gradeable || card.legs.length} boosts on track`;
}

function kickoffLabel(ms: number): string {
  const d = new Date(ms);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase();
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} ${time}`;
}

/**
 * Build the per-card view-models for an entry. Settled cards use the stamped
 * `cardScore`; live cards score the running scoreline through the pure engine, so the
 * board projects exactly what the server would settle if play stopped now.
 */
export function buildCardVMs(
  entry: Entry,
  fixtures: Map<string, Fixture>,
  now: number = Date.now(),
  round?: Pick<Round, 'pickMode' | 'firstKickoffAt' | 'status'>
): CardVM[] {
  // Entry lock (spec §14.1). Football is FLEXIBLE: per-fixture. The entry as a whole
  // stays editable while the round is OPEN/LOCKING (so a player who backed only an early
  // match can still build a card on a later, still-open one); each card then freezes at
  // its OWN kickoff via the per-card `editable` flag below. The no-`round` fallback is
  // only hit by already-settled views (every fixture terminal anyway).
  const pickedKickoffs = entry.predictions
    .map((p) => fixtures.get(p.fixtureId)?.kickoffAt)
    .filter((k): k is number => k != null);
  const cardLocked = round
    ? !isEntryEditable(round, pickedKickoffs, now)
    : entry.predictions.some((p) => {
        const f = fixtures.get(p.fixtureId);
        return f != null && (f.status !== 'SCHEDULED' || now >= f.kickoffAt);
      });
  return entry.predictions.map((p) => {
    const f = fixtures.get(p.fixtureId);
    const multiplier = cardMultiplier(p.legs ?? []);
    const toWin = Math.round(BASE * multiplier);
    if (!f) {
      const legs = (p.legs ?? []).map((l) => legVM(l, null, false));
      return {
        fixtureId: p.fixtureId,
        fixtureLabel: p.fixtureId,
        homeTeam: '',
        awayTeam: '',
        homeCode: '',
        awayCode: '',
        outcome: p.outcome,
        outcomeLabel: outcomeLabel(p.outcome, 'Home', 'Away'),
        base: BASE,
        anchorState: 'open',
        legs,
        markets: dotRow('open', legs),
        multiplier,
        toWin,
        atRisk: -BASE,
        marketSummary: 'Awaiting fixture',
        trail: [],
        points: null,
        livePoints: null,
        status: 'upcoming',
        kickoffAt: 0,
        editable: false
      };
    }

    const home = teamCode(f.homeTeam, f.homeCode);
    const away = teamCode(f.awayTeam, f.awayCode);
    const terminal = isTerminal(f) && !!f.result && f.status !== 'VOID';
    const live = f.status === 'LIVE';
    const editable = !cardLocked && f.status === 'SCHEDULED' && now < f.kickoffAt;

    let status: PickStatus = 'upcoming';
    if (f.status === 'VOID') status = 'void';
    else if (live) status = 'live';
    else if (terminal) status = 'final';

    // Provisional result from the running scoreline while in play, so the board lights
    // up exactly as the server would settle it if play stopped now.
    const liveH = f.liveHomeGoals ?? 0;
    const liveA = f.liveAwayGoals ?? 0;
    const liveHt =
      f.liveHalftimeHome != null && f.liveHalftimeAway != null
        ? { home: f.liveHalftimeHome, away: f.liveHalftimeAway }
        : null;
    const liveResult = live
      ? deriveResult(f.fixtureId, liveH, liveA, scorerIdsFromEvents(f.events), liveHt)
      : null;

    const fixtureLabel = terminal
      ? `${home} ${f.result!.homeGoals}–${f.result!.awayGoals} ${away}`
      : `${home} v ${away}`;
    const outLabel = outcomeLabel(p.outcome, f.homeTeam, f.awayTeam);

    const gradingResult = status === 'final' ? f.result! : status === 'live' ? liveResult! : null;
    const legs = (p.legs ?? []).map((l) => legVM(l, gradingResult, status === 'live'));
    let anchorState: MarketHit = 'open';
    if (gradingResult) {
      const hit = anchorHit(p, gradingResult);
      anchorState = hit ? (status === 'live' ? 'ontrack' : 'hit') : status === 'live' ? 'offtrack' : 'miss';
    }

    const points = terminal ? p.cardScore ?? cardScore(p, f.result!) : null;
    const livePoints = status === 'live' ? cardScore(p, liveResult!) : null;
    const trail = terminal ? buildTrail(p, f.result!, legs, outLabel) : [];

    const card: CardVM = {
      fixtureId: p.fixtureId,
      fixtureLabel,
      homeTeam: f.homeTeam,
      awayTeam: f.awayTeam,
      homeCode: home,
      awayCode: away,
      homeLogo: f.homeLogo,
      awayLogo: f.awayLogo,
      outcome: p.outcome,
      outcomeLabel: outLabel,
      base: BASE,
      anchorState,
      legs,
      markets: dotRow(anchorState, legs),
      multiplier,
      toWin,
      atRisk: -BASE,
      marketSummary: '',
      formula: `${BASE} × ${formatMultiplier(multiplier)}`,
      trail,
      points,
      livePoints,
      status,
      kickoffAt: f.kickoffAt,
      kickoffLabel: status === 'upcoming' ? kickoffLabel(f.kickoffAt) : undefined,
      liveScore: status === 'live' ? `${liveH}–${liveA}` : undefined,
      liveClock: status === 'live' ? (f.elapsed != null ? `${f.elapsed}'` : 'LIVE') : undefined,
      editable
    };

    if (status === 'void') card.marketSummary = 'Void · excluded';
    else if (status === 'final') card.marketSummary = finalSummary(card);
    else if (status === 'live') card.marketSummary = liveSummary(card);
    else card.marketSummary = editable ? 'Editable until kickoff' : 'Locked';

    return card;
  });
}

/** Count of an entry's picks whose fixture is currently in play. */
export function fixturesLive(entry: Entry, fixtures: Map<string, Fixture>): number {
  return entry.predictions.reduce((n, p) => {
    const f = fixtures.get(p.fixtureId);
    return f && f.status === 'LIVE' ? n + 1 : n;
  }, 0);
}

/** Count of an entry's picks whose fixture has reached a terminal state. */
export function fixturesPlayed(entry: Entry, fixtures: Map<string, Fixture>): number {
  return entry.predictions.reduce((n, p) => {
    const f = fixtures.get(p.fixtureId);
    return f && isTerminal(f) ? n + 1 : n;
  }, 0);
}
